/**
 * ============================================================================
 * Iron Meridian - Game Layout Component
 * ============================================================================
 * 
 * Main three-panel layout with map in center, info panels on sides.
 * Desktop-first design with fixed sidebars and dominant center map.
 */

import { useState, useEffect, useCallback } from 'react'
import WorldMap from './WorldMap'
import CountryPanel from './CountryPanel'
import ActionPanel from './ActionPanel'
import ActionQueue from './ActionQueue'
import PlayerPanel from './PlayerPanel'
import CombatLog from './CombatLog'
import TurnControl from './TurnControl'
import DiplomacyModal from './DiplomacyModal'
import TechTreePanel from './TechTreePanel'
import PostGameAnalytics from './PostGameAnalytics'
import ConnectionStatusBadge from './ConnectionStatusBadge'
import { AudioSettings, AudioToggleButton } from './AudioSettings'
import { useAudio } from '../audio/hooks/useAudio.js'
import { getTechState, startResearch, cancelResearch } from '../services/api'

export default function GameLayout({
  gameState,
  countries,
  selectedCountry,
  actionQueue,
  combatLog,
  turnProcessing,
  onCountrySelect,
  onAddAction,
  onRemoveAction,
  onClearActions,
  onProcessTurn,
  onNewGame,
  onReturnToMenu,
  // Diplomacy props
  players,
  diplomacyState,
  pendingProposals,
  onProposeTreaty,
  onAcceptProposal,
  onRejectProposal,
  onCancelTreaty
}) {
  const selectedCountryData = selectedCountry ? countries[selectedCountry] : null
  const playerCountryData = gameState?.playerCountry ? countries[gameState.playerCountry] : null
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false)
  const [techTreeOpen, setTechTreeOpen] = useState(false)
  const [diplomacyOpen, setDiplomacyOpen] = useState(false)
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const { playAmbient, stopAmbient, playMusic, stopMusic, isReady: audioReady } = useAudio()

  // Check if game ended and auto-open analytics
  useEffect(() => {
    if (gameState?.status && ['victory', 'defeat', 'draw'].includes(gameState.status)) {
      setAnalyticsOpen(true)
    }
  }, [gameState?.status])

  // Function to manually open analytics (can be called anytime)
  const handleOpenAnalytics = () => {
    if (gameState?.gameId && gameState?.currentTurn > 5) {
      setAnalyticsOpen(true)
    } else {
      alert('Not enough game data yet. Play at least 5 turns for analytics.')
    }
  }

  // Research state from API
  const [researchState, setResearchState] = useState({
    researched: [],
    currentResearch: null,
    researchPoints: 0,
    researchHistory: []
  })

  // Fetch research state from API
  const fetchResearchState = useCallback(async () => {
    try {
      const result = await getTechState()
      if (result.success && result.data) {
        setResearchState({
          researched: result.data.researched || [],
          currentResearch: result.data.currentResearch,
          researchPoints: result.data.researchPoints || 0,
          researchHistory: result.data.researchHistory || [],
          completedTechs: result.data.researched || []
        })
      }
    } catch (error) {
      console.log('Failed to fetch tech state:', error)
    }
  }, [])

  // Load research state on mount
  useEffect(() => {
    fetchResearchState()
  }, [fetchResearchState])

  // Refresh research state when gameState.currentTurn changes (turn was processed)
  useEffect(() => {
    if (gameState?.currentTurn > 1) {
      fetchResearchState()
    }
  }, [gameState?.currentTurn, fetchResearchState])

  // Handler for starting research
  const handleStartResearch = async (techId) => {
    console.log('handleStartResearch called with:', techId)
    try {
      const result = await startResearch(techId)
      console.log('startResearch API result:', result)
      if (result.success) {
        // Refresh research state to show the update
        await fetchResearchState()
        // Don't close modal - let user see the research started
        console.log('Research started successfully:', techId)
      } else {
        console.error('Failed to start research:', result.error)
        alert(`Failed to start research: ${result.error}`)
      }
    } catch (error) {
      console.error('Error starting research:', error)
      alert(`Error starting research: ${error.message}`)
    }
  }

  // Handler for canceling research
  const handleCancelResearch = async () => {
    try {
      const result = await cancelResearch()
      if (result.success) {
        await fetchResearchState()
      }
    } catch (error) {
      console.error('Error canceling research:', error)
    }
  }

  // Keyboard shortcut for tech tree (T key)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      
      if (e.key === 't' || e.key === 'T') {
        setTechTreeOpen(prev => !prev)
      }
      if (e.key === 'd' || e.key === 'D') {
        setDiplomacyOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Start ambient audio and music when game loads (after first user interaction)
  useEffect(() => {
    if (audioReady && gameState?.status === 'active') {
      playAmbient('warRoom')
      playMusic('strategic')
    }
    return () => {
      stopAmbient()
      stopMusic()
    }
  }, [audioReady, gameState?.status, playAmbient, stopAmbient, playMusic, stopMusic])

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-war-room-900">
      {/* Audio Settings Modal */}
      <AudioSettings 
        isOpen={audioSettingsOpen} 
        onClose={() => setAudioSettingsOpen(false)} 
      />
      {/* Tech Tree Modal */}
      <TechTreePanel
        isOpen={techTreeOpen}
        onClose={() => setTechTreeOpen(false)}
        researchState={researchState}
        onStartResearch={handleStartResearch}
        onCancelResearch={handleCancelResearch}
        playerEconomy={playerCountryData?.economy || 0}
      />
      {/* Diplomacy Modal */}
      <DiplomacyModal
        isOpen={diplomacyOpen}
        onClose={() => setDiplomacyOpen(false)}
        diplomacyState={diplomacyState || { relationships: {} }}
        countries={countries}
        playerCountry={gameState?.playerCountry}
        pendingProposals={pendingProposals || []}
        onProposeTreaty={onProposeTreaty}
        onAcceptProposal={onAcceptProposal}
        onRejectProposal={onRejectProposal}
        onCancelTreaty={onCancelTreaty}
      />
      {/* Post-Game Analytics Modal */}
      <PostGameAnalytics
        isOpen={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        gameId={gameState?.gameId}
        onJumpToTurn={(turn) => {
          setAnalyticsOpen(false)
          // Open replay at specific turn
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('openReplay', { 
              detail: { gameId: gameState?.gameId, initialTurn: turn } 
            }))
          }
        }}
      />
      {/* Left Panel - Player Info & Country Details */}
      <aside className="w-80 flex-shrink-0 flex flex-col bg-gradient-to-b from-war-room-800 via-war-room-850 to-war-room-900 border-r border-war-room-600/50 shadow-2xl animate-slide-in-left z-10">
        {/* Player Status */}
        <PlayerPanel 
          gameState={gameState} 
          playerCountry={playerCountryData}
          onNewGame={onNewGame}
          onReturnToMenu={onReturnToMenu}
        />
        
        {/* Selected Country - Scrollable */}
        <div className="flex-1 overflow-y-auto custom-scrollbar" data-tutorial="country-panel">
          <CountryPanel 
            country={selectedCountryData}
            isPlayerOwned={selectedCountryData?.controller === 'player'}
            isHomeCountry={selectedCountry === gameState?.playerCountry}
            playerCountry={gameState?.playerCountry}
            diplomacyEntry={diplomacyState?.relationships?.[selectedCountry]}
            onOpenDiplomacy={() => setDiplomacyOpen(true)}
          />
        </div>
      </aside>

      {/* Center - World Map (Dominant Area) */}
      <main className="flex-1 relative overflow-hidden" data-tutorial="world-map">
        <WorldMap
          countries={countries}
          selectedCountry={selectedCountry}
          playerCountry={gameState?.playerCountry}
          onCountrySelect={onCountrySelect}
          diplomacy={diplomacyState?.relationships}
        />
        
        {/* Turn indicator overlay - centered at top */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20">
          <div className="bg-war-room-800/95 backdrop-blur-sm px-8 py-3 rounded-2xl border border-war-room-600/50 shadow-xl flex items-center gap-4">
            {/* Connection Status Badge */}
            <ConnectionStatusBadge />
            <div className="w-px h-10 bg-war-room-600/50"></div>
            <div className="flex flex-col items-center">
              <span className="text-xs text-gray-500 uppercase tracking-wider">Turn</span>
              <span className="text-3xl font-bold text-war-room-accent tabular-nums">{gameState?.currentTurn || 1}</span>
            </div>
            <div className="w-px h-10 bg-war-room-600/50"></div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 uppercase tracking-wider">Phase</span>
              <span className="text-sm font-medium text-gray-300">
                {gameState?.phase === 'player_action' ? 'Planning' : 'Resolution'}
              </span>
            </div>
            <div className="w-px h-10 bg-war-room-600/50"></div>
            <AudioToggleButton onClick={() => setAudioSettingsOpen(true)} />
          </div>
        </div>
      </main>

      {/* Right Panel - Actions & Combat Log */}
      <aside className="w-96 flex-shrink-0 flex flex-col bg-gradient-to-b from-war-room-800 via-war-room-850 to-war-room-900 border-l border-war-room-600/50 shadow-2xl animate-slide-in-right z-10">
        {/* Turn Control */}
        <div data-tutorial="turn-control">
          <TurnControl
            gameState={gameState}
            actionCount={actionQueue.length}
            isProcessing={turnProcessing}
            onProcessTurn={onProcessTurn}
          />
        </div>
        
        {/* Action Panel */}
        <div data-tutorial="action-panel">
          <ActionPanel
            selectedCountry={selectedCountryData}
            playerCountry={playerCountryData}
            countries={countries}
            gameState={gameState}
            onAddAction={onAddAction}
            onOpenTechTree={() => setTechTreeOpen(true)}
            techTreeOpen={techTreeOpen}
            onOpenDiplomacy={() => setDiplomacyOpen(true)}
            diplomacyOpen={diplomacyOpen}
            pendingProposals={pendingProposals}
          />
        </div>
        
        {/* Action Queue */}
        <ActionQueue
          actions={actionQueue}
          countries={countries}
          onRemoveAction={onRemoveAction}
          onClearActions={onClearActions}
        />
        
        {/* Combat Log - Scrollable */}
        <div className="flex-1 overflow-y-auto custom-scrollbar border-t border-war-room-600/30">
          <CombatLog entries={combatLog} countries={countries} />
        </div>
      </aside>
    </div>
  )
}
